import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, ScrollView, TextInput, Modal, Platform, Animated, PanResponder } from 'react-native';
import { useSelector } from 'react-redux';
import { scale } from '@components/frame';
import ColorWrapper from '@styles/colors';
import { isPhone, isWeb } from '@helpers';
import SignalRLogViewer from './SignalRLogViewer';
import useSignalRDebugger from './useSignalRDebugger';

const SignalRDebugger = ({ C }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState<'logs' | 'connections' | 'stats'>('logs');

  // Floating button animation (removed pulse for now)

  // Draggable position for mobile
  const pan = useRef(new Animated.ValueXY({ x: 20, y: 100 })).current;

  const { connectionStatus, eventLogs, statistics, unreadCount, clearUnread, exportLogs } = useSignalRDebugger();

  // Redux selectors
  const signalRState = useSelector((state: any) => state.signalR);
  const { appHubID, signalRAnonymID, appHubReconnecting, signalRAnonymReconnecting } = signalRState;
  const machines = useSelector((state: any) => state.machines?.machines || []);

  // Removed pulse animation and badge

  // Pan responder for draggable button on mobile
  const panResponder = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: () => !isWeb && !isOpen,
      onPanResponderGrant: () => {
        pan.setOffset({
          x: (pan.x as any)._value,
          y: (pan.y as any)._value,
        });
      },
      onPanResponderMove: Animated.event([null, { dx: pan.x, dy: pan.y }], {
        useNativeDriver: false,
      }),
      onPanResponderRelease: () => {
        pan.flattenOffset();
      },
    })
  ).current;

  const handleOpen = () => {
    console.log('SignalR Debugger: Opening panel, current state:', isOpen);
    setIsOpen(true);
    clearUnread();
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const colors = {
    background: C('Background / Light / Detail [mobile]') || '#f5f5f5',
    panel: C('Background/Light/Field [mobile]') || '#ffffff',
    text: C('Label / Light / Primary') || '#000000',
    textSecondary: C('Label / Light / Secondary') || '#666666',
    border: C('Hairline/Light/No trans') || '#e0e0e0',
    primary: C('Selected/Light/Primary') || '#007bff',
    success: '#28a745',
    error: '#dc3545',
    warning: '#ffc107',
    buttonBg: C('Tint / Purple / Fill') || '#6200ea',
    buttonText: C('Button/Light/Primary Text') || '#ffffff',
  };

  const styles = {
    floatingButton: {
      position: 'absolute' as const,
      width: scale(40),
      height: scale(40),
      borderRadius: scale(20),
      backgroundColor: colors.buttonBg,
      justifyContent: 'center' as const,
      alignItems: 'center' as const,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 4,
      elevation: 5,
      zIndex: 9999,
    },
    floatingButtonWeb: {
      position: 'fixed' as const,
      right: scale(20),
      bottom: scale(20),
    },
    buttonText: {
      fontSize: scale(18),
      color: colors.buttonText,
      fontWeight: '700' as const,
    },
    modal: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    panel: {
      position: 'absolute' as const,
      bottom: 0,
      left: 0,
      right: 0,
      maxHeight: '80%',
      backgroundColor: colors.panel,
      borderTopLeftRadius: scale(20),
      borderTopRightRadius: scale(20),
      shadowColor: '#000',
      shadowOffset: { width: 0, height: -2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 10,
    },
    webPanel: {
      position: 'fixed' as const,
      right: scale(20),
      bottom: scale(70),
      width: scale(400),
      maxHeight: '70vh',
      backgroundColor: colors.panel,
      borderRadius: scale(12),
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column' as const,
    },
    header: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'space-between' as const,
      padding: scale(16),
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    headerTitle: {
      fontSize: scale(18),
      fontWeight: '700' as const,
      color: colors.text,
    },
    closeButton: {
      padding: scale(8),
    },
    closeButtonText: {
      fontSize: scale(16),
      color: colors.textSecondary,
    },
    tabBar: {
      flexDirection: 'row' as const,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    tab: {
      flex: 1,
      paddingVertical: scale(12),
      alignItems: 'center' as const,
    },
    activeTab: {
      borderBottomWidth: scale(2),
      borderBottomColor: colors.primary,
    },
    tabText: {
      fontSize: scale(14),
      fontWeight: '600' as const,
      color: colors.textSecondary,
    },
    activeTabText: {
      color: colors.primary,
    },
    content: {
      flex: 1,
      padding: scale(16),
    },
    connectionStatus: {
      marginBottom: scale(16),
    },
    connectionRow: {
      flexDirection: 'row' as const,
      justifyContent: 'space-between' as const,
      alignItems: 'center' as const,
      paddingVertical: scale(8),
      paddingHorizontal: scale(12),
      backgroundColor: colors.background,
      borderRadius: scale(8),
      marginBottom: scale(8),
    },
    connectionLabel: {
      fontSize: scale(13),
      fontWeight: '600' as const,
      color: colors.text,
    },
    connectionValue: {
      fontSize: scale(12),
      color: colors.textSecondary,
      fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
    },
    statusIndicator: {
      width: scale(8),
      height: scale(8),
      borderRadius: scale(4),
      marginLeft: scale(8),
    },
    searchBar: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      backgroundColor: colors.background,
      borderRadius: scale(8),
      paddingHorizontal: scale(12),
      marginBottom: scale(12),
    },
    searchInput: {
      flex: 1,
      paddingVertical: scale(8),
      fontSize: scale(14),
      color: colors.text,
    },
    statsGrid: {
      flexDirection: 'row' as const,
      flexWrap: 'wrap' as const,
      marginHorizontal: scale(-6),
    },
    statCard: {
      width: '50%',
      padding: scale(6),
    },
    statCardInner: {
      backgroundColor: colors.background,
      borderRadius: scale(8),
      padding: scale(12),
      alignItems: 'center' as const,
    },
    statValue: {
      fontSize: scale(20),
      fontWeight: '700' as const,
      color: colors.text,
      marginBottom: scale(4),
    },
    statLabel: {
      fontSize: scale(11),
      color: colors.textSecondary,
      textAlign: 'center' as const,
    },
    exportButton: {
      backgroundColor: colors.primary,
      paddingVertical: scale(10),
      paddingHorizontal: scale(20),
      borderRadius: scale(8),
      alignItems: 'center' as const,
      marginTop: scale(12),
    },
    exportButtonText: {
      color: colors.buttonText,
      fontSize: scale(14),
      fontWeight: '600' as const,
    },
  };

  const renderConnectionStatus = () => (
    <View style={styles.connectionStatus}>
      <View style={styles.connectionRow}>
        <Text style={styles.connectionLabel}>AppHub</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text style={styles.connectionValue}>{appHubID || 'Not connected'}</Text>
          <View
            style={[
              styles.statusIndicator,
              {
                backgroundColor: appHubID ? (appHubReconnecting ? colors.warning : colors.success) : colors.error,
              },
            ]}
          />
        </View>
      </View>
      <View style={styles.connectionRow}>
        <Text style={styles.connectionLabel}>Anonymous</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text style={styles.connectionValue}>{signalRAnonymID || 'Not connected'}</Text>
          <View
            style={[
              styles.statusIndicator,
              {
                backgroundColor: signalRAnonymID ? (signalRAnonymReconnecting ? colors.warning : colors.success) : colors.error,
              },
            ]}
          />
        </View>
      </View>
    </View>
  );

  const renderStats = () => (
    <View>
      <View style={styles.statsGrid}>
        <View style={styles.statCard}>
          <View style={styles.statCardInner}>
            <Text style={styles.statValue}>{statistics.totalEvents}</Text>
            <Text style={styles.statLabel}>Total Events</Text>
          </View>
        </View>
        <View style={styles.statCard}>
          <View style={styles.statCardInner}>
            <Text style={styles.statValue}>{statistics.eventsPerMinute}</Text>
            <Text style={styles.statLabel}>Events/Min</Text>
          </View>
        </View>
        <View style={styles.statCard}>
          <View style={styles.statCardInner}>
            <Text style={styles.statValue}>{statistics.uniqueEventTypes}</Text>
            <Text style={styles.statLabel}>Event Types</Text>
          </View>
        </View>
        <View style={styles.statCard}>
          <View style={styles.statCardInner}>
            <Text style={styles.statValue}>{statistics.lastEventAge}</Text>
            <Text style={styles.statLabel}>Last Event</Text>
          </View>
        </View>
      </View>
      <TouchableOpacity style={styles.exportButton} onPress={exportLogs}>
        <Text style={styles.exportButtonText}>Export Logs</Text>
      </TouchableOpacity>
    </View>
  );

  const renderPanel = () => {
    console.log('SignalR Debugger: Rendering panel, eventLogs:', eventLogs.length);
    return (
      <View style={isWeb ? styles.webPanel : styles.panel}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>SignalR Debugger</Text>
          <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.tabBar}>
          <TouchableOpacity style={[styles.tab, selectedTab === 'logs' && styles.activeTab]} onPress={() => setSelectedTab('logs')}>
            <Text style={[styles.tabText, selectedTab === 'logs' && styles.activeTabText]}>Logs ({eventLogs.length})</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.tab, selectedTab === 'connections' && styles.activeTab]} onPress={() => setSelectedTab('connections')}>
            <Text style={[styles.tabText, selectedTab === 'connections' && styles.activeTabText]}>Connections</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.tab, selectedTab === 'stats' && styles.activeTab]} onPress={() => setSelectedTab('stats')}>
            <Text style={[styles.tabText, selectedTab === 'stats' && styles.activeTabText]}>Stats</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.content}>
          {selectedTab === 'logs' && (
            <>
              <View style={styles.searchBar}>
                <TextInput
                  style={styles.searchInput}
                  placeholder="Search events..."
                  placeholderTextColor={colors.textSecondary}
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                />
              </View>
              <SignalRLogViewer logs={eventLogs} searchQuery={searchQuery} colors={colors} machines={machines} />
            </>
          )}
          {selectedTab === 'connections' && renderConnectionStatus()}
          {selectedTab === 'stats' && renderStats()}
        </View>
      </View>
    );
  };

  const floatingButtonStyle = isWeb
    ? [styles.floatingButton, styles.floatingButtonWeb]
    : [
        styles.floatingButton,
        {
          transform: [{ translateX: pan.x }, { translateY: pan.y }],
        },
      ];

  return (
    <>
      <Animated.View style={floatingButtonStyle as any} {...(!isWeb && panResponder.panHandlers)}>
        <TouchableOpacity onPress={handleOpen} style={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
          <Text style={styles.buttonText}>S</Text>
        </TouchableOpacity>
      </Animated.View>

      {isOpen &&
        (() => {
          console.log('SignalR Debugger: isOpen is true, rendering panel for', isWeb ? 'web' : 'mobile');
          return isWeb ? (
            renderPanel()
          ) : (
            <Modal
              visible={isOpen}
              transparent
              animationType="slide"
              onRequestClose={handleClose}
              supportedOrientations={['portrait', 'portrait-upside-down', 'landscape', 'landscape-left', 'landscape-right']}
            >
              <TouchableOpacity style={styles.modal} activeOpacity={1} onPress={handleClose}>
                <TouchableOpacity activeOpacity={1} onPress={(e) => e.stopPropagation()}>
                  {renderPanel()}
                </TouchableOpacity>
              </TouchableOpacity>
            </Modal>
          );
        })()}
    </>
  );
};

export default ColorWrapper(SignalRDebugger);
